import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import EventForm, { EventFormValues } from '@/components/classes/EventForm';
import { ClassMutationService } from '@/services/classService';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Plus, X, Loader2 } from 'lucide-react';
import { getLocations } from '@/services/locationService';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const NewEventPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const organizationId = user?.user_metadata?.organization_id || '';

  useEffect(() => {
    const fetchOptions = async () => {
      if (!organizationId) return;
      setOptionsLoading(true);
      try {
        const locations = await getLocations(organizationId);
        setLocationOptions(locations.map(loc => ({ value: loc.id, label: loc.name })));
      } catch (err) {
        toast.error('Failed to load location options');
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, [organizationId]);

  const addEventMutation = useMutation({
    mutationFn: (data: any) => ClassMutationService.createClass(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      toast.success('Event created successfully!');
      navigate('/classes');
    },
    onError: (error: any) => {
      console.error('Error creating event:', error);
      toast.error('Failed to create event. Please try again.');
    },
  });

  const onSubmitEvent = (values: any) => {
    if (!organizationId) {
      toast.error('Missing organization ID. Please log in again.');
      return;
    }
    const eventData = {
      name: values.name,
      description: values.description || '',
      location_id: values.location_id === 'none' || values.location_id === 'virtual' || values.location_id === 'offsite' ? null : values.location_id ?? null,
      organization_id: organizationId,
      is_event: true,
      event_date: values.startDate ? format(values.startDate, 'yyyy-MM-dd') : null,
      event_start_time: values.startTime || null,
      event_end_time: values.endTime || null,
      billing_model: values.billing_model,
      price_cents: values.price_cents,
      max_students: values.capacity ?? null,
      category: values.category || null,
      status: values.status || 'active',
      trial_available: values.trial_available || false,
      is_active: typeof values.is_active === 'boolean' ? values.is_active : true,
      // schedules: [] // For future: add schedule support for events if needed
    };
    addEventMutation.mutate(eventData);
  };

  const handleCancel = () => {
    navigate('/admin/classes');
  };

  if (optionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50">
        <div className="container mx-auto py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading form options...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Classes
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-orange-100 to-purple-100 shadow-sm">
              <Calendar className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Event</h1>
              <p className="text-gray-600 mt-1">Set up a new event with schedule, pricing, and details</p>
            </div>
          </div>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-4xl mx-auto"
        >
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-orange-600 to-purple-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="h-6 w-6" />
                  <div>
                    <CardTitle className="text-xl">New Event Setup</CardTitle>
                    <p className="text-orange-100 text-sm">Fill in the details to create your new event</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={addEventMutation.isPending}
                    className="text-white hover:bg-white/10"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <EventForm
                onSubmit={onSubmitEvent}
                onCancel={handleCancel}
                isSubmitting={addEventMutation.isPending}
                locationOptions={locationOptions}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default NewEventPage; 